import { useCallback, useMemo } from 'react';
import { useKmDomains, useKmEntityTypes } from './api/hooks';

/** Отбор справочников для показа: только действующие записи, весь список одним окном */
const DICTIONARY_PARAMS = { includeDeleted: false, limit: 1000 };

/**
 * Справочники модели знаний для показа: действующие типы сущностей и домены,
 * а также разрешение их кодов в названия.
 *
 * Экземпляры и типы несут только коды, поэтому название берётся отсюда — одним правилом
 * на все экраны: название из справочника, а вне его (например, у мягко удалённого кода) —
 * сам код. Оба запроса живут в общем кэше, так что несколько потребителей на экране
 * дают те же два запроса, что и один.
 */
export const useKmDictionaries = () => {
  const { data: typesData } = useKmEntityTypes(DICTIONARY_PARAMS);
  const { data: domainsData } = useKmDomains(DICTIONARY_PARAMS);

  const entityTypes = useMemo(() => typesData?.items ?? [], [typesData]);
  const domains = useMemo(() => domainsData?.items ?? [], [domainsData]);

  const typeNames = useMemo(() => new Map(entityTypes.map((item) => [item.code, item.name])), [entityTypes]);
  const domainNames = useMemo(() => new Map(domains.map((item) => [item.code, item.name])), [domains]);

  const getEntityTypeName = useCallback((code: string) => typeNames.get(code) ?? code, [typeNames]);
  const getDomainName = useCallback((code: string) => domainNames.get(code) ?? code, [domainNames]);

  return { entityTypes, domains, getEntityTypeName, getDomainName };
};
